/**
 * Error thrown when the Delopay API returns a non-2xx response, or when a
 * timeout or network error occurs.
 *
 * @example
 * ```typescript
 * try {
 *   await delopay.payments.create({ amount: 5000, currency: 'EUR' });
 * } catch (e) {
 *   if (e instanceof DelopayError) {
 *     console.error(e.status, e.code, e.requestId, e.message);
 *   }
 * }
 * ```
 */
export class DelopayError extends Error {
  /** HTTP status code returned by the API, or `0` for timeout/network errors. */
  readonly status: number;
  /** Machine-readable error code returned by the API (e.g. `'HE_00'`). */
  readonly code: string;
  /** Error category (e.g. `'invalid_request'`, `'timeout_error'`). */
  readonly type: string;
  /** Value of the `x-request-id` response header, when present. Include this when contacting support. */
  readonly requestId?: string;
  /**
   * Raw response body (truncated to ~2 KB). Populated when the server returns a
   * non-JSON error body (e.g. an HTML 502 from an upstream proxy) so debugging
   * still has something to go on.
   */
  readonly rawBody?: string;
  /**
   * Structured error context the API attaches under `error.data` for select
   * codes — e.g. `{ retry_after_secs: 248 }` on rate-limit / max-attempt
   * lockouts. Schema is per-code; consult the API reference for the shape.
   */
  readonly data?: Record<string, unknown>;

  constructor(
    message: string,
    options: {
      status: number;
      code: string;
      type: string;
      requestId?: string;
      rawBody?: string;
      data?: Record<string, unknown>;
    },
  ) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'DelopayError';
    this.status = options.status;
    this.code = options.code;
    this.type = options.type;
    if (options.requestId !== undefined) this.requestId = options.requestId;
    if (options.rawBody !== undefined) this.rawBody = options.rawBody;
    if (options.data !== undefined) this.data = options.data;
  }
}

/**
 * Thrown when the API key is missing, invalid, or revoked (HTTP 401).
 *
 * @example
 * ```typescript
 * if (e instanceof DelopayAuthenticationError) {
 *   // Prompt user to re-enter their API key.
 * }
 * ```
 */
export class DelopayAuthenticationError extends DelopayError {
  constructor(
    message = 'Invalid API key',
    options?: {
      code?: string;
      type?: string;
      requestId?: string;
      rawBody?: string;
      data?: Record<string, unknown>;
    },
  ) {
    super(message, {
      status: 401,
      // Default to the generic "invalid API key" code, but let callers pass
      // through the server-reported code (e.g. `UR_05` for unverified-email
      // 401s) so they can differentiate between auth failure reasons.
      code: options?.code || 'AUTH_01',
      type: options?.type || 'authentication_error',
      requestId: options?.requestId,
      rawBody: options?.rawBody,
      data: options?.data,
    });
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'DelopayAuthenticationError';
  }
}
